import { ViewIds, type ArdoqId } from '@ardoq/api-types';
import { componentGraphSelectionChanged } from '@ardoq/component-selection';
import { dispatchAction } from '@ardoq/rxbeach';
import { openDetailsDrawer } from 'appLayout/ardoqStudio/detailsDrawer/actions';
import { setContextMenuState } from 'contextMenus/contextMenuState$';
import { componentSelectedInPresentationMode } from 'streams/components/ComponentActions';
import { componentSelectedInQuickExploreMode } from 'traverseOneLevel/actions';
import { handleStoreSetting } from 'viewSettings/settingsHelper';
import type { FlexibleBlockDiagramCommands } from './types';
import { flexibleBlockDiagramSetReadOnly } from './actions';

const VIEW_ID = ViewIds.FLEXIBLE_BLOCK_DIAGRAM;

const updateGraphSelection = (componentIds: ArdoqId[]) =>
  dispatchAction(
    componentGraphSelectionChanged({ graphSelection: componentIds })
  );

const saveCollapsedGroupIds = (collapsedGroupIds: string[]) =>
  handleStoreSetting(VIEW_ID, { collapsedGroupIds });

const setReadOnly = (viewInstanceId: string) => (isReadOnly: boolean) =>
  dispatchAction(flexibleBlockDiagramSetReadOnly(viewInstanceId, isReadOnly));

const closeContextMenu = () => dispatchAction(setContextMenuState(null));

const selectComponentInPresentationMode = (componentId: ArdoqId) =>
  dispatchAction(componentSelectedInPresentationMode({ id: componentId }));

const selectComponentInQuickExploreMode = (componentId: ArdoqId) =>
  dispatchAction(componentSelectedInQuickExploreMode(componentId));

const openComponentInDetailsDrawer = (componentId: ArdoqId) =>
  dispatchAction(openDetailsDrawer([componentId]));

export type ViewpointModeFlexibleBlockDiagramCommandsConfig = {
  isPresentationMode: boolean;
  isQuickExploreSelectionEnabled: boolean;
};

export const getComponentDoubleClickHandler = ({
  isPresentationMode,
  isQuickExploreSelectionEnabled,
}: ViewpointModeFlexibleBlockDiagramCommandsConfig): FlexibleBlockDiagramCommands['onComponentDoubleClick'] => {
  if (isPresentationMode) {
    return selectComponentInPresentationMode;
  }
  if (isQuickExploreSelectionEnabled) {
    return selectComponentInQuickExploreMode;
  }
  return openComponentInDetailsDrawer;
};

/**
 * Design decision: opening URLs (context menu "Open URL") is deliberately not a
 * command. A URL opens the only way the app knows how, so making it
 * configurable would only add code complexity — threading a callback through
 * the context-menu builders — with no host that needs a different behaviour.
 */
export const createViewpointModeFlexibleBlockDiagramCommands = (
  config: ViewpointModeFlexibleBlockDiagramCommandsConfig & {
    viewInstanceId: string;
  }
): FlexibleBlockDiagramCommands => ({
  onSelectionChange: updateGraphSelection,
  onComponentDoubleClick: getComponentDoubleClickHandler(config),
  onCollapsedGroupsChange: saveCollapsedGroupIds,
  onReadOnlyChange: setReadOnly(config.viewInstanceId),
  closeContextMenu,
});
